import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import {
  CreateEventResponse,
  requestCreateEvent,
  EventType,
} from '@puzzlehq/sdk';
import { extractAmountFromInputString, raffleFees, RaffleV2 } from 'jigsaw';
import { RecordWithPlaintext } from '@puzzlehq/types';
import { LoadingStatus } from '@src/data/utils';

export const enum Step {
  _01_Buy,
  _02_Confirmed,
}

type zBuyGiveawayState = {
  status: LoadingStatus;
  step: Step;
  entries: number;
  ticket: RecordWithPlaintext | undefined;
  createEventResponse?: CreateEventResponse;
  close: () => void;
  setStep: (step: Step) => void;
  setEntries: (entries: number) => void;
  setTicket: (ticket: RecordWithPlaintext) => void;
  sendRequest: (giveaway: RaffleV2) => Promise<CreateEventResponse>;
};

export const useBuyGiveawayStore = create<zBuyGiveawayState>()(
  immer((set, get) => ({
    status: 'idle',
    step: Step._01_Buy,
    entries: 0,
    ticket: undefined,
    close: async () => {
      await new Promise((f) => setTimeout(f, 500));
      set({
        status: 'idle',
        step: Step._01_Buy,
        entries: 1,
      });
    },
    setStep: (step: Step) => {
      set({ step });
    },
    setEntries: (entries: number) => {
      set({ entries });
    },
    setTicket: (ticket: RecordWithPlaintext) => {
      set({ ticket });
    },
    sendRequest: async (giveaway: RaffleV2) => {
      const entries = get().entries;
      const ticket = get().ticket;

      if (!entries || entries < 0) {
        set({ status: 'failed' });
        return {
          error: 'Invalid entries value',
        };
      } else if (!ticket) {
        set({ status: 'failed' });
        return {
          error: 'No ticket provided!',
        };
      } else if (
        entries * 1_000_000 >
        (extractAmountFromInputString(ticket.data.amount) ?? 0)
      ) {
        return {
          error: `Invalid ticket value. ${entries} entries. ${ticket.data.amount} ticket.data.amount`,
        };
      }
      set({ status: 'loading' });
      try {
        console.log(`buying ${entries} raffle entries with ticket`, ticket);
        let inputs = [ticket.plaintext.replace(/\s+/g, '')];
        if (!!giveaway.giveawayId) {
          inputs = [giveaway.giveawayId, ticket.plaintext.replace(/\s+/g, '')];
        }
        const fee =
          giveaway.fees?.[entries.toString()] ??
          raffleFees[entries.toString()] ??
          1.7;

        console.log(fee);
        console.log(giveaway.fees?.[entries.toString()]);
        console.log(raffleFees[entries.toString()]);
        const response = await requestCreateEvent({
          programId: giveaway.programId,
          functionId: giveaway.entryFunctions[entries],
          fee,
          inputs,
          type: EventType.GiveawayEntry,
        });

        console.log(response);

        const createEventResponse: CreateEventResponse = {
          eventId: response.eventId,
          error: response.error,
        };
        set({
          status: createEventResponse.eventId ? 'succeeded' : 'failed',
          createEventResponse,
        });
        return createEventResponse;
      } catch (e) {
        set({ status: 'failed' });
        console.error(e);
        return {
          error: (e as Error).message,
        };
      }
    },
  }))
);
